import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa6";
import toast from "react-hot-toast";
import Spinner from "../../spinner";
import Button from "../../button-v1";
import Tooltip from "../../shared-modules/Tooltip";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  getAllStatusOptions,
  getPatientStatuses,
  createPatientStatus,
  deletePatientStatus,
} from "../../../services/patient/settings";
import styles from "./StatusesModal.module.css";

const StatusesModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Track pending deletions only (adds are saved immediately)
  const [pendingDeletes, setPendingDeletes] = useState([]);

  const { data: availableStatuses, isLoading: isLoadingGlobalStatuses } = useQuery(
    ["global-statuses"],
    () => getAllStatusOptions(),
    {
      enabled: isOpen,
      onError: (err) => {
        console.error("Could not load statuses:", err);
        toast.error("Could not load status. Please try again.");
      },
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: patientStatuses, isLoading: isPatientStatusesLoading } = useQuery(
    ["patient-statuses"],
    () => getPatientStatuses(),
    {
      enabled: isOpen,
      onError: (err) => {
        console.error("Could not load patient statuses:", err);
        toast.error("Could not load patient statuses. Please try again.");
      },
    }
  );

  const createStatusMutation = useMutation(
    (data) => createPatientStatus(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-statuses"]);
        resetForm();
        toast.success("Status added successfully");
      },
      onError: (error) => {
        console.error("Could not create status:", error);
        toast.error("Failed to add status. Please try again.");
      },
    }
  );

  const deleteStatusMutation = useMutation(
    (statusId) => deletePatientStatus(statusId),
    {
      onSuccess: () => {
        toast.success("Status removed successfully");
      },
      onError: (error) => {
        console.error("Could not delete status:", error);
        toast.error("Could not remove status. Please try again.");
      },
    }
  );

  // Display statuses excluding marked for deletion
  const displayStatuses = useMemo(() => {
    const existing = patientStatuses || [];
    return existing.filter((s) => !pendingDeletes.includes(s.id));
  }, [patientStatuses, pendingDeletes]);

  // Filter out already assigned statuses
  const assignedStatusIds = useMemo(
    () => displayStatuses?.map((s) => s.status) || [],
    [displayStatuses]
  );

  const filteredStatuses = useMemo(
    () => availableStatuses?.filter((s) => !assignedStatusIds.includes(s.id)),
    [availableStatuses, assignedStatusIds]
  );

  const getStatusName = (statusId) => {
    return availableStatuses?.find((s) => s.id === statusId)?.name || "";
  };

  const resetForm = () => {
    setSelectedStatus("");
    setDescription("");
    setShowAddForm(false);
  };

  const handleClose = () => {
    resetForm();
    setPendingDeletes([]);
    onClose();
  };

  // Add new status - calls API immediately
  const handleAddStatus = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }

    const data = {
      status: Number(selectedStatus),
      description: description.trim(),
    };

    createStatusMutation.mutate(data);
  };

  // Mark for deletion (not deleted yet)
  const handleMarkForDelete = (statusId) => {
    if (pendingDeletes.includes(statusId)) {
      // Unmark if already marked
      setPendingDeletes(pendingDeletes.filter((id) => id !== statusId));
    } else {
      setPendingDeletes([...pendingDeletes, statusId]);
    }
  };

  // Save deletions only
  const handleSave = async () => {
    if (pendingDeletes.length === 0) {
      handleClose();
      return;
    }

    setIsSaving(true);

    try {
      // Execute all deletes
      for (const statusId of pendingDeletes) {
        await deletePatientStatus(statusId);
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(["patient-statuses"]);

      toast.success("Changes saved successfully");
      handleClose();
    } catch (error) {
      console.error("Could not save changes:", error);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const isMarkedForDeletion = (statusId) => {
    return pendingDeletes.includes(statusId);
  };

  return (
    <Dialog open={isOpen} setOpen={handleClose}>
      <DialogContent className={styles.modalContent}>
        <DialogHeader>
          <DialogTitle>Edit Statuses</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoadingGlobalStatuses || isPatientStatusesLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.modalBody}>
              {/* Current Statuses Section */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Current statuses</h3>
                <div className={styles.statusPillsContainer}>
                  {patientStatuses && patientStatuses.length > 0 ? (
                    patientStatuses.map((patientStatus) => {
                      const statusName = getStatusName(patientStatus.status);
                      const isDeleting = isMarkedForDeletion(patientStatus.id);

                      return (
                        <div
                          key={patientStatus.id}
                          className={`${styles.statusPill} ${isDeleting ? styles.statusPillDeleting : ""}`}
                        >
                          <span className={styles.statusPillText}>{statusName}</span>
                          <Tooltip content={patientStatus.description || "No description"} position="top" zIndexOffset="100000">
                            <span className={styles.statusInfoDot}>i</span>
                          </Tooltip>
                          <button
                            type="button"
                            className={`${styles.deleteIcon} ${isDeleting ? styles.deleteIconMarked : ""}`}
                            onClick={() => handleMarkForDelete(patientStatus.id)}
                            title={isDeleting ? "Undo delete" : "Delete status"}
                          >
                            {isDeleting ? "↶" : "×"}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <p className={styles.emptyMessage}>No statuses assigned yet</p>
                  )}
                </div>
              </div>

              {/* Add New Status Section */}
              <div className={styles.section}>
                {!showAddForm ? (
                  <button
                    type="button"
                    className={styles.addStatusButton}
                    onClick={() => setShowAddForm(true)}
                  >
                    <FaPlus size={12} />
                    <span>Add new status</span>
                  </button>
                ) : (
                  <div className={styles.form}>
                    <div className={styles.formGroup}>
                      <label htmlFor="status-name">Status name</label>
                      <Select
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                      >
                        <SelectTrigger id="status-name" className={styles.selectTrigger}>
                          <SelectValue>
                            {
                              filteredStatuses?.find(
                                (status) => String(status.id) === String(selectedStatus)
                              )?.name || "Select a status"
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {filteredStatuses?.map((status) => (
                            <SelectItem key={status.id} value={String(status.id)}>
                              {status.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="status-description">Information related to the new status</label>
                      <textarea
                        id="status-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter description"
                        rows={3}
                        className={styles.textarea}
                      />
                    </div>

                    <div className={styles.formActions}>
                      <Button
                        type="button"
                        onClick={handleAddStatus}
                        disabled={!selectedStatus || !description.trim()}
                        variant="primary"
                        loading={createStatusMutation.isLoading}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogBody>
        {!showAddForm && (
          <DialogFooter>
            <Button
              type="button"
              variant="normal"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={pendingDeletes.length === 0}
            >
              Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatusesModal;
